import { useEffect, useRef, useState } from "react";

/* useApiSource — common loader for every Terminal pane.

   - Calls `fetcher(token)` on mount and on the configured interval.
   - On success → status = "live", data = response payload.
   - On 404 → status = "feature_off" (server says feature flag is off),
     data = the provided fallback (mock).
   - On 5xx / network failure → status = "degraded", data = fallback.
   - When token is missing → status = "loading", data = fallback.

   Per the project's <graceful_and_controlled_degradation> rule, read
   endpoints never break the UI; the pane reads `data` and the parent
   can render the status badge so users know whether they're looking
   at live or cached data. */

export default function useApiSource({
  token,
  fetcher,
  fallback,
  intervalMs = 0,
  pickData = (resp) => resp?.data,
} = {}) {
  const [data, setData] = useState(fallback);
  const [status, setStatus] = useState(token ? "loading" : "fallback");
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    if (!token || !fetcherRef.current) {
      setStatus("fallback");
      setData(fallback);
      return () => {};
    }

    let intervalId = null;
    const tick = async () => {
      try {
        const resp = await fetcherRef.current(token);
        if (!mountedRef.current) return;
        const payload = pickData(resp);
        // Spec convention: { code, message, ... }. code !== 0 = app-level error.
        if (payload && typeof payload === "object" && "code" in payload && payload.code !== 0) {
          setStatus("degraded");
          setData(fallback);
          return;
        }
        setStatus("live");
        setData(payload);
      } catch (err) {
        if (!mountedRef.current) return;
        const code = err?.response?.status;
        if (code === 404) {
          setStatus("feature_off");
        } else {
          setStatus("degraded");
        }
        setData(fallback);
      }
    };

    tick();
    if (intervalMs > 0) intervalId = setInterval(tick, intervalMs);
    return () => {
      mountedRef.current = false;
      if (intervalId) clearInterval(intervalId);
    };
  }, [token, intervalMs, fallback, pickData]);

  return { data, status };
}
